import { DataFarmType } from 'src/app/domainObjects/dataFarmType';
import { PreviewDataImportRequest } from 'src/app/messaging/previewImportDataRequest';
import { ConfigService } from 'src/app/services/config/config.service';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { TransformerViewModel } from '../../../transformer-list/transformer-list.component';
import { EventNamerRuleViewModel } from './eventNamerRuleViewModel';
import { ColumnFilter } from 'src/app/domainObjects/columnFilter';
import { TransformDetailViewModelBase } from 'src/app/views/dataTransformConfig/transformers/base/base-transformer-ui/transformDetailViewModelBase';
import { SortInfo } from '../../../../domainObjects/sortInfo';
import { DataDictionaryService } from 'src/app/services/dataDictionary/data-dictionary.service';
import { ValidationInfo, RequiredFieldValidation } from 'src/app/objectModel/validationInfo';
import { TransformerParametersViewModel } from '../transformer-parameters/transformerParametersViewModel';
import { IncludeInfo } from 'src/app/domainObjects/includeInfo';
import { ParameterSetting } from 'src/app/domainObjects/masterConfig';
import { TransformType } from 'src/app/domainObjects/transformType';
import { EventNamerTransform, EventNamerTransformConfig } from 'src/app/domainObjects/transformers/eventNamerTransform';
import { EventNamerPreviewDataResponse } from 'src/app/messaging/eventNamerPreviewDataResponse';

export class EventNamerViewModel extends TransformDetailViewModelBase<EventNamerTransform> {
    farmType: DataFarmType;
    sourceColumn: string;
    targetColumn: string;
    userColumn: string;
    timeColumn: string;
    sampleStart: number;
    sampleEnd: number;
    runAll: boolean;
    deleteMisses = false;
    missName: string;
    threads: number;
    searchWildCard: string;
    multiTargetColumn: boolean;
    spaceSeparator: boolean;
    removeRedundant: boolean;

    rules: Array<EventNamerRuleViewModel> = [];

    parameters:TransformerParametersViewModel=null;
    // Preview options
    rows: number;
    // Status
    hitCount?: number;
    missCount?: number;
    hitRatio?: number;
    rowsDisplay?: string;
    hitRatioDisplay?: string;
  
    request: PreviewDataImportRequest;
    response: EventNamerPreviewDataResponse;
  
    constructor(
      dataDictService: DataDictionaryService,
      private configService: ConfigService,
      configViewModel: DataTransformConfigViewModel,
      transformViewModel: TransformerViewModel) {

        super(dataDictService, configViewModel, transformViewModel);

        const transformConfig = this.transform.transformConfig;
        this.parameters=new TransformerParametersViewModel();
        if (transformConfig) {
          this.farmType = transformConfig.farmType ?? transformViewModel.transform.dataFarmType;
          this.sourceColumn = transformConfig.defSourceField;
          this.userColumn = transformConfig.userField;
          this.targetColumn = transformConfig.defTargetField;
          this.deleteMisses = transformConfig.delMisses;
          this.missName = transformConfig.missesNamed;
          this.sampleEnd = transformConfig.sampleEnd + 1;
          this.sampleStart = transformConfig.sampleStart + 1;
          this.timeColumn = transformConfig.timestampField;
          this.runAll = transformConfig.runAll;
          this.sortInfo = transformConfig.sortInfo;
          if(transformConfig.includeInResults){
            this.includeInResults = transformConfig.includeInResults.map(item =>new IncludeInfo(item));
            this.parameters.includeInResults=this.includeInResults;
          }
          //parameters
          this.parameters.sourceColumn= transformConfig.defSourceField;
          this.parameters.targetColumn= transformConfig.defTargetField;
          this.parameters.userColumn= transformConfig.userField;
          this.parameters.timeColumn= transformConfig.timestampField;
          this.parameters.sortInfo= transformConfig.sortInfo;
          //this.parameters.includeInResults= transformConfig.in;
          this.parameters.sampleStart = transformConfig.sampleStart + 1;
          this.parameters.sampleEnd = transformConfig.sampleEnd + 1;
          this.parameters.runAll= transformConfig.runAll;
          this.parameters.deleteMisses = transformConfig.delMisses;
          this.parameters.missName= transformConfig.missesNamed;
          // advanced settings
          this.searchWildCard = transformConfig.searchWildCard;
          this.threads = transformConfig.threads;
          this.multiTargetColumn = transformConfig.multiColumnTarget;
          this.spaceSeparator = transformConfig.spaceSeparator;
          this.removeRedundant = transformConfig.removeRedundant;
          //this.groupBy = transformConfig.groupBy;

          if (transformConfig.rules) {
            this.rules = transformConfig.rules.map(r => new EventNamerRuleViewModel(r));
          }

          // default
          if (!this.timeColumn || this.timeColumn?.length === 0) {
            this.timeColumn = this.getDefaultTimestampColumn();
          }
        }
    }

    override configureValidationInfo(): ValidationInfo[] {
      const infos = [];
      infos.push( new RequiredFieldValidation('threads', 'Threads (0 > Auto)'));
      return infos;
    }

    public override onTransformRunCompleted(response: EventNamerPreviewDataResponse): void {
      // A cache-hit run carries no stats at all (they're deliberately stripped
      // before a CachedProcessingResult is persisted -- see
      // project_stats_vs_fullrun_separation memory). Nothing has changed since
      // whatever last populated the stats currently on screen, so they're still
      // accurate -- leave them exactly as they are instead of wiping them blank.
      if (
        !response?.previewStats ||
        !response?.totalStats ||
        !response.totalStats.globalStat ||
        !response.totalStats.ruleStats ||
        !response.previewStats.globalStat ||
        !response.previewStats.ruleStats
      ) {
        return;
      }

      // Clear all stats prior to rebinding new values
      this.hitCount = null;
      this.hitRatio = null;
      this.missCount = null;
      this.hitRatioDisplay ="0.00%";
      this.rows = 0;
      this.rules.forEach( r => {
        r.hits = null;
        r.sampleHits = null;
      });

      if (this.parameters.runAll) {
        // Map Transform Global and Rule stats
        this.hitCount = response.totalStats?.globalStat.rowHits;

        if (response.totalStats?.globalStat.hitRate) {
          this.hitRatio = response.totalStats.globalStat.hitRate;
          this.hitRatioDisplay = `${(this.hitRatio * 100).toFixed(2)}%`;
        }
        this.missCount = response.totalStats?.globalStat.rowMisses;
      } else {
        // Map Transform Global and Rule stats
        this.hitCount = response.previewStats.globalStat.rowHits;

        if (response.previewStats.globalStat.hitRate) {
          this.hitRatio = response.previewStats.globalStat.hitRate;
          this.hitRatioDisplay = `${(this.hitRatio * 100).toFixed(2)}%`;
        }
        this.missCount = response.previewStats.globalStat.rowMisses;
      }

      this.rows = this.parameters.runAll ? response.currentData?.totalRows : response.activeData?.totalRows;
      // map the rule stats to the rule
      if (response.previewStats?.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          this.rules[i].sampleHits = response.previewStats.ruleStats.find(r => r.ruleIndex === i)?.rowHits;
        }
      }

      if (response.totalStats?.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          const stat = response.totalStats.ruleStats.find(r => r.ruleIndex === i);
          this.rules[i].hits = stat?.rowHits;
        }
      }
    }

    reindexRules(): void {
      for (let i = 0; i < this.rules.length; i++) {
        this.rules[i].index = i + 1;
      }
      this.rules.sort((a, b) => a.index - b.index);
    }

    override toTransformConfig(): EventNamerTransformConfig {
      const config = new EventNamerTransformConfig();
      config.farmType = this.farmType;
      config.transformType = TransformType.eventNamer;
      config.defSourceField=this.parameters.sourceColumn;
      config.defTargetField=this.parameters.targetColumn;
      config.userField=this.parameters.userColumn;
      config.timestampField=this.parameters.timeColumn;
      if(this.parameters.sortInfo){
        if(this.parameters.sortInfo.length==0){
          config.sortInfo =undefined;
        }else{
          config.sortInfo=this.parameters.sortInfo;
        }
      }
      if(this.parameters.includeInResults){
        if(this.parameters.includeInResults.length==0){
          config.includeInResults =undefined;
        }else{
          config.includeInResults =this.parameters.includeInResults.map(item => item.includeField);
        }
      }
      config.sampleStart =this.parameters.sampleStart-1;
      config.sampleEnd =this.parameters.sampleEnd-1;
      config.runAll=this.parameters.runAll;
      config.delMisses=this.parameters.deleteMisses;
      config.missesNamed=this.parameters.missName;

      config.threads = this.threads;
      config.searchWildCard = this.searchWildCard;
      config.multiColumnTarget = this.multiTargetColumn;
      config.spaceSeparator = this.spaceSeparator;
      config.removeRedundant = this.removeRedundant;

      config.rules = this.rules.map(r => {
        const rule= r.toEventNamerRule();

        return rule;
      });

      return config;
    }
  }
